import { HOST_URL } from "config";
import type { AmbassadorDashboardResult } from "api/ambassadorApi";
import type {
  ReferralAdminPerformanceMetrics,
  ReferralAdminPerformanceOverviewResult,
  ReferralAdminPerformanceRow,
} from "api/adminApi";

// 로컬(localhost) 전용 성과 목 데이터: 어드민 성과 화면을 서버·실데이터 없이 그대로 확인하기 위한 것이다.
// dev/real 빌드는 HOST_URL 이 localhost 가 아니므로 자동으로 꺼지고 실제 API 를 탄다.
// 켜져 있을 때는 화면에 "목 데이터" 배지를 띄워 실데이터로 오인하지 않게 한다.
export const PERFORMANCE_MOCK_ENABLED = HOST_URL.includes("localhost");

// 서버 개요와 같은 범위: 기여 기반인 앰버서더(affiliate) 코드만 둔다.
type MockCreator = {
  code: string;
  name: string;
  groupNo: number | null;
  perPayerAmount: number | null;
  // 하루 가입자 수의 상한: 크리에이터별 규모 차이를 만든다
  scale: number;
  // 링크 종료일. 지난 날짜면 이 코드는 더 이상 유입이 없다
  endDate: string | null;
};

const MOCK_CREATORS: MockCreator[] = [
  {
    code: "minji",
    name: "김민지",
    groupNo: 3,
    perPayerAmount: 20000,
    scale: 9,
    endDate: null,
  },
  {
    code: "seojun",
    name: "박서준",
    groupNo: 3,
    perPayerAmount: 20000,
    scale: 6,
    endDate: null,
  },
  {
    code: "haneul",
    name: "이하늘",
    groupNo: 2,
    perPayerAmount: 15000,
    scale: 4,
    endDate: null,
  },
  {
    code: "doyun",
    name: "정도윤",
    groupNo: -1,
    perPayerAmount: 20000,
    scale: 3,
    endDate: null,
  },
  {
    code: "yuna",
    name: "최유나",
    groupNo: 1,
    perPayerAmount: 15000,
    scale: 2,
    endDate: "2026-06-30",
  },
  {
    code: "sohee",
    name: "한소희",
    groupNo: null,
    perPayerAmount: null,
    scale: 1,
    endDate: null,
  },
];

// 결제 1건당 결제액: 3개월 정기결제 정가를 가정한 고정값 (목 데이터는 할인 조합까지 흉내내지 않는다)
const MOCK_PAY_AMOUNT = 490000;

// 코드+날짜로 결정되는 의사난수(FNV-1a): 새로고침해도 같은 날의 숫자가 흔들리지 않아야
// "어제보다 늘었나"를 눈으로 확인할 수 있다.
const seeded = (key: string) => {
  let hash = 2166136261;
  for (let i = 0; i < key.length; i += 1) {
    hash ^= key.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return ((hash >>> 0) % 10000) / 10000;
};

// 하루치 이벤트: 결제·환불은 가입에서 파생시켜 전환율이 그럴듯한 범위(0~40%)에 들어오게 한다.
const dayEvents = (creator: MockCreator, date: string) => {
  if (creator.endDate && date > creator.endDate) {
    return { signupCount: 0, payerCount: 0, refundCount: 0 };
  }
  const signupCount = Math.round(
    seeded(`${creator.code}:s:${date}`) * creator.scale
  );
  const payerCount = Math.round(
    signupCount * seeded(`${creator.code}:p:${date}`) * 0.4
  );
  const refundCount = seeded(`${creator.code}:r:${date}`) > 0.93 ? 1 : 0;
  return { signupCount, payerCount, refundCount };
};

const eachDate = (from: string, to: string) => {
  const dates: string[] = [];
  for (let cursor = from; cursor <= to; cursor = shiftDate(cursor, 1)) {
    dates.push(cursor);
    if (dates.length > 800) break; // 잘못된 범위로 무한 루프를 돌지 않게 하는 안전장치
  }
  return dates;
};

const shiftDate = (date: string, days: number) => {
  const d = new Date(`${date}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
};

const localToday = () => {
  const d = new Date();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${month}-${day}`;
};

const metricsOf = (
  creators: MockCreator[],
  from: string,
  to: string
): ReferralAdminPerformanceMetrics => {
  let signupCount = 0;
  let payerCount = 0;
  let refundCount = 0;
  // 수수료가 설정된 코드가 하나라도 있으면 활동이 없어도 0원이고, 하나도 없을 때만 미산출(null)이다
  // 서버(performanceOverview)·정산 표와 같은 규칙.
  let settlementAmount: number | null = creators.some(
    (creator) => creator.perPayerAmount != null
  )
    ? 0
    : null;
  eachDate(from, to).forEach((date) => {
    creators.forEach((creator) => {
      const day = dayEvents(creator, date);
      signupCount += day.signupCount;
      payerCount += day.payerCount;
      refundCount += day.refundCount;
      if (creator.perPayerAmount != null) {
        settlementAmount =
          (settlementAmount ?? 0) +
          (day.payerCount - day.refundCount) * creator.perPayerAmount;
      }
    });
  });
  return {
    signupCount,
    payerCount,
    refundCount,
    revenueAmount: payerCount * MOCK_PAY_AMOUNT,
    settlementAmount,
  };
};

const rowOf = (
  creator: MockCreator,
  from: string,
  to: string
): ReferralAdminPerformanceRow => ({
  code: creator.code,
  name: creator.name,
  groupNo: creator.groupNo,
  perPayerAmount: creator.perPayerAmount,
  metrics: metricsOf([creator], from, to),
  trend: eachDate(shiftDate(to, -13), to).map(
    (date) => dayEvents(creator, date).payerCount
  ),
});

export const mockPerformanceOverview = (
  from: string,
  to: string
): ReferralAdminPerformanceOverviewResult => {
  const today = localToday();
  const yesterday = shiftDate(today, -1);
  const lastMonthSameDay = shiftDate(`${today.slice(0, 8)}01`, -1);
  return {
    from,
    to,
    rows: MOCK_CREATORS.map((creator) => rowOf(creator, from, to)),
    live: {
      asOf: new Date().toISOString(),
      today: metricsOf(MOCK_CREATORS, today, today),
      yesterday: metricsOf(MOCK_CREATORS, yesterday, yesterday),
      thisMonth: metricsOf(MOCK_CREATORS, `${today.slice(0, 7)}-01`, today),
      lastMonthToDate: metricsOf(
        MOCK_CREATORS,
        `${lastMonthSameDay.slice(0, 7)}-01`,
        // 지난달 같은 일자: 말일이 짧으면 그 달 말일로 잘린다
        `${lastMonthSameDay.slice(0, 7)}-${today.slice(8)}` <= lastMonthSameDay
          ? `${lastMonthSameDay.slice(0, 7)}-${today.slice(8)}`
          : lastMonthSameDay
      ),
    },
  };
};

// 상세 팝업(기존 /performance API) 목 데이터: 최근 90일을 일별·월별로 접어 보여준다.
export const mockPerformanceDetail = (
  code: string
): AmbassadorDashboardResult => {
  const creator =
    MOCK_CREATORS.find((item) => item.code === code) ?? MOCK_CREATORS[0];
  const today = localToday();
  const dates = eachDate(shiftDate(today, -89), today);

  const daily = dates
    .map((date) => ({ period: date, ...dayEvents(creator, date) }))
    .filter(
      (bucket) =>
        bucket.signupCount + bucket.payerCount + bucket.refundCount > 0
    )
    .reverse();

  const monthlyMap = new Map<
    string,
    { signupCount: number; payerCount: number; refundCount: number }
  >();
  daily.forEach((bucket) => {
    const month = bucket.period.slice(0, 7);
    const acc = monthlyMap.get(month) ?? {
      signupCount: 0,
      payerCount: 0,
      refundCount: 0,
    };
    acc.signupCount += bucket.signupCount;
    acc.payerCount += bucket.payerCount;
    acc.refundCount += bucket.refundCount;
    monthlyMap.set(month, acc);
  });

  return {
    name: creator.name,
    codes: [creator.code],
    total: {
      signupCount: daily.reduce((sum, b) => sum + b.signupCount, 0),
      payerCount: daily.reduce((sum, b) => sum + b.payerCount, 0),
      refundCount: daily.reduce((sum, b) => sum + b.refundCount, 0),
    },
    daily: daily.map(({ period, signupCount, payerCount, refundCount }) => ({
      period,
      signupCount,
      payerCount,
      refundCount,
    })),
    monthly: Array.from(monthlyMap.entries())
      .sort((a, b) => b[0].localeCompare(a[0]))
      .map(([period, acc]) => ({
        period,
        ...acc,
        settlementAmount:
          creator.perPayerAmount != null
            ? (acc.payerCount - acc.refundCount) * creator.perPayerAmount
            : null,
      })),
  };
};
